import { Component, Inject, OnDestroy, TemplateRef, ViewChild, ViewContainerRef } from '@angular/core';
import { Observable } from 'rxjs';
import { LocatedVesselRow, PlaybackState } from '../playback-controller';
import { ToolbarDrawerService } from '../toolbar-drawer.service';
import { ToolbarControlContext, TOOLBAR_CONTROL_CONTEXT } from '../toolbar-control.tokens';
import { VoyageVesselController } from '../voyage-vessel.controller';
import { BaseControl } from './base-control';

/**
 * The search-history control — ONE button for every vessel worldwide search
 * has put on the map.
 *
 * It replaced a seven-control per-vessel bar section (title/trace/center/
 * drift/dim/info/close): a bar's worth of chrome to narrate one hull was the
 * QA complaint, and everything a located vessel actually needs is already in
 * the map's normal grammar — clicking its marker opens the popup and makes it
 * the selection, which is what the ordinary Trace button then acts on. What the grammar could
 * NOT do was list, recentre and remove them, so that — and only that — is
 * what this control's drawer carries.
 *
 * It renders in two bars: the Voyage Radar LIVE bar (snapshot drawn), and the
 * standalone `vessel` slot on an empty map, where there is no live bar to
 * join. To ride either, it takes state and actions from the root-provided
 * `VoyageVesselController` directly instead of from the host's controller —
 * the track-entry button's cross-controller injection pattern — and renders
 * nothing until a search has located something.
 */

/** "12 min ago" / "2 h ago" / "6 d ago" — the fix's age, in the coarsest unit. */
export function relativeFixAge(iso: string | null | undefined): string | null {
  if (!iso) return null;
  const t = new Date(iso).getTime();
  if (isNaN(t)) return null;
  const mins = Math.max(0, Math.round((Date.now() - t) / 60000));
  if (mins < 60) return `${mins} min ago`;
  if (mins < 48 * 60) return `${Math.round(mins / 60)} h ago`;
  return `${Math.round(mins / 1440)} d ago`;
}

@Component({
  selector: 'app-vessel-history-control',
  template: `
    <ng-container *ngIf="(state$ | async)?.locatedVessels as list">
      <button *ngIf="list.length" #trigger type="button" class="tb-btn"
              [class.tb-btn--active]="drawer.isOpen(panel)"
              (click)="drawer.toggle(panel, vcr, trigger)"
              aria-label="Watched Vessels"
              [matTooltip]="'Watched Vessels · ' + list.length + ' on the map'"
              matTooltipPosition="above">
        <mat-icon class="material-icons-outlined">bookmarks</mat-icon>
      </button>
    </ng-container>

    <!-- Outside the *ngIf, like every drawer panel here: the open overlay
         holds this TemplateRef, and destroying its declaring view while the
         drawer is open leaves a detached panel on screen. -->
    <ng-template #panel>
      <!-- "Clear all" is a header action rather than a row: it is the one act
           the per-row buttons below cannot express, and it inherited this
           drawer when the sensor panel's ON MAP summary cards were removed.
           That stack's saved-vessels row was its only home — every other thing
           those cards carried already existed elsewhere (snapshot download and
           browse in Refresh History, track download in Vessel Tracks, dim on
           the map toolbar, and the snapshot layer itself clears when the sensor
           checkbox is unticked). This is the drawer that lists exactly the
           vessels it acts on, so it is where the act belongs. -->
      <app-toolbar-drawer *ngIf="(state$ | async)?.locatedVessels as list"
                          title="Watched Vessels"
                          [subtitle]="list.length + ' on the map'"
                          actionLabel="Clear all"
                          [actionDisabled]="!list.length"
                          (action)="clearAll()"
                          (close)="drawer.close()">
        <div class="vh-list">
          <div class="vh-row" *ngFor="let r of list; trackBy: trackRow">
            <span class="vh-row__diamond" [class.is-stale]="r.stale"></span>
            <span class="vh-row__text">
              <span class="vh-row__name">{{ r.name || 'MMSI ' + r.mmsi }}</span>
              <span class="vh-row__age" *ngIf="age(r) as a">
                {{ r.stale ? 'last known · ' : 'fix · ' }}{{ a }}
              </span>
            </span>
            <button type="button" class="vh-row__btn"
                    (click)="vessel.centerVessel(r.mmsi)"
                    aria-label="Center on vessel"
                    matTooltip="Center the map on this vessel" matTooltipPosition="above">
              <mat-icon class="material-icons-outlined">my_location</mat-icon>
            </button>
            <!-- This drawer lists what is ON THE MAP, so its row action clears
                 the map and nothing else — deleting a saved vessel is the
                 panel's job, behind its own labelled button. The layers_clear
                 glyph is what both ON MAP bars use for taking a layer down. -->
            <button type="button" class="vh-row__btn"
                    (click)="vessel.takeOffMap(r.mmsi)"
                    aria-label="Take vessel off the map"
                    matTooltip="Take off the map — stays in Watched Vessels" matTooltipPosition="above">
              <mat-icon class="material-icons-outlined">layers_clear</mat-icon>
            </button>
          </div>
        </div>
      </app-toolbar-drawer>
    </ng-template>`,
  styles: [`
    .vh-list {
      display: flex;
      flex-direction: column;
      max-height: 260px;
      overflow-y: auto;
    }
    .vh-row {
      display: flex;
      align-items: center;
      gap: 8px;
      padding: 6px 2px;
    }
    .vh-row + .vh-row { border-top: 1px solid rgba(128, 128, 128, 0.18); }
    .vh-row__diamond {
      flex: 0 0 auto;
      width: 9px;
      height: 9px;
      background: #B45309;
      transform: rotate(45deg);
    }
    .vh-row__diamond.is-stale {
      background: transparent;
      border: 1.5px solid #9aa0a6;
    }
    .vh-row__text {
      flex: 1 1 auto;
      min-width: 0;
      display: flex;
      flex-direction: column;
      line-height: 1.25;
    }
    .vh-row__name {
      font-size: 12px;
      font-weight: 600;
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
    }
    .vh-row__age {
      font-size: 10.5px;
      opacity: 0.65;
    }
    .vh-row__btn {
      flex: 0 0 auto;
      display: inline-flex;
      align-items: center;
      justify-content: center;
      width: 26px;
      height: 26px;
      border: none;
      border-radius: 6px;
      background: transparent;
      color: inherit;
      cursor: pointer;
      padding: 0;
    }
    .vh-row__btn:hover { background: rgba(128, 128, 128, 0.15); }
    .vh-row__btn .mat-icon {
      font-size: 16px;
      width: 16px;
      height: 16px;
      line-height: 16px;
    }
  `],
})
export class VesselHistoryControlComponent extends BaseControl implements OnDestroy {
  @ViewChild('panel') panel!: TemplateRef<any>;

  constructor(
    @Inject(TOOLBAR_CONTROL_CONTEXT) public context: ToolbarControlContext,
    public vessel: VoyageVesselController,
    public drawer: ToolbarDrawerService,
    public vcr: ViewContainerRef,
  ) {
    super();
  }

  /** Self-sourced, so the control works whichever bar hosts it. */
  override get state$(): Observable<PlaybackState> {
    return this.vessel.state$;
  }

  /** Take every listed marker off the map. Records are untouched — see the
   *  note on the header action in the template. */
  clearAll(): void {
    this.vessel.clearAll();
    this.drawer.close();
  }

  age(r: LocatedVesselRow): string | null {
    return relativeFixAge(r.timeUtc);
  }

  trackRow(_i: number, r: LocatedVesselRow): string {
    return r.mmsi;
  }

  ngOnDestroy(): void {
    this.drawer.closeIfOpen(this.panel);
  }
}
